import React, {
    ReactElement, ReactNode, useEffect, useState,
} from "react";
import { Card } from "react-bootstrap";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { RiArrowDownSLine, RiArrowRightSLine, RiArrowUpSLine } from "@remixicon/react";
import classNames from "classnames";
import { useTheme } from "../../contexts/Theme";
import Button from "../theme/button/Button";
import Icon from "../theme/icon/Icon";

export default function ExpandingSection({
    title, children, defaultExpanded, className, topRightButton, topRightClicked,
    classBackground, cardClassName, onCollapsed, onExpanded, defaultCardClassName, buttonClassName,
    iconVariant, shouldWatchExpansion,
}: {
    title: ReactNode;
    defaultExpanded?: boolean;
    children: ReactElement[] | ReactElement | false | undefined | ReactNode;
    classBackground?: string;
    className?: string;
    cardClassName?: string;
    topRightButton?: ReactElement;
    defaultCardClassName?: string;
    buttonClassName?: string;
    topRightClicked?: () => void;
    onExpanded?: () => void;
    onCollapsed?: () => void;
    iconVariant?: "subtle" | "small"
    shouldWatchExpansion?: boolean;
}): ReactElement {
    const [isExpanded, setExpanded] = useState(defaultExpanded);

    useEffect(() => {
        if (shouldWatchExpansion) {
            setExpanded(defaultExpanded);
        }
    }, [defaultExpanded, shouldWatchExpansion]);

    const toggle = () => {
        setExpanded(current => !current);
        if (isExpanded) {
            onCollapsed?.();
        } else {
            onExpanded?.();
        }
    };
    const theme = useTheme();
    return (
        <Card className={classNames((!theme.isV3 ? "mb-3" : defaultCardClassName), cardClassName)}>
            <Card.Title
                className={classNames({
                    "p-2": !theme.isV3,
                    "p3t-1 p3b-05": theme.isV3,
                    "m-0 d-flex justify-content-between": true,
                }, classBackground ?? (theme.isV3 ? "" : "bg-gray-300"))}
            >
                {theme.isV3 ? (
                    <Button
                        onClick={toggle}
                        variant="none"
                        className={buttonClassName}
                    >
                        {title}
                        {iconVariant === "small" && (
                            <Icon
                                variant="default"
                                size="small"
                                icon={isExpanded ? <RiArrowUpSLine /> : <RiArrowRightSLine />}
                            />
                        )}
                        {iconVariant === "subtle" && (
                            isExpanded ? <RiArrowUpSLine className="icon-subtle" /> : <RiArrowDownSLine className="icon-subtle" />
                        )}
                    </Button>
                ) : (
                    <button
                        type="button"
                        onClick={toggle}
                        className="text-start btn btn-none w-100 p-0 m-0"
                    >
                        {isExpanded ? <FiChevronRight /> : <FiChevronDown />}
                        {" "}
                        {title}
                    </button>
                )}

                {topRightButton && (
                    <button
                        type="button"
                        onClick={topRightClicked}
                        className="btn btn-none p-0 m-0"
                    >
                        {topRightButton}
                    </button>
                )}

            </Card.Title>
            {isExpanded && (
                <Card.Body
                    className={classNames(className, {
                        "p3x-0 p3y-1": theme.isV3,
                    })}
                >
                    {children}
                </Card.Body>
            )}
        </Card>
    );
}
ExpandingSection.defaultProps = {
    defaultExpanded: false,
    className: "",
    topRightButton: undefined,
    topRightClicked: undefined,
    classBackground: undefined,
    cardClassName: undefined,
    onCollapsed: undefined,
    onExpanded: undefined,
    buttonClassName: "text-start p3-0 w-100 d-flex justify-content-between align-items-center text-body-1-semibold",
    defaultCardClassName: "card-transparent border-0 p3x-2 p3y-1",
    iconVariant: "subtle",
    shouldWatchExpansion: false,
};
